import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import clsx from 'clsx';
import { LayoutGroup, motion, useReducedMotion } from 'motion/react';
import { useId } from 'react';

const sizeStyles = {
  xs: 'p-0.5 [--toggle-icon-size:--spacing(3)] [--toggle-px:--spacing(2.5)] [--toggle-py:--spacing(1)] [&>button]:text-xs',
  sm: 'p-0.75 [--toggle-icon-size:--spacing(3.75)] [--toggle-px:--spacing(3)] [--toggle-py:--spacing(1.5)]',
  md: 'p-0.75 [--toggle-icon-size:--spacing(4)] [--toggle-px:--spacing(3.5)] [--toggle-py:--spacing(2)]',
  lg: 'p-1 [--toggle-icon-size:--spacing(5)] [--toggle-px:--spacing(4)] [--toggle-py:--spacing(2.5)]'
};

const hoverStyles = clsx(
  '[--toggle-hover-overlap:calc(var(--toggle-py)+0.5lh)]',
  '[&>[data-pressed]+button:not([data-disabled]):hover]:rounded-s-none [&>[data-pressed]+button:not([data-disabled]):hover]:before:-inset-s-(--toggle-hover-overlap)',
  '[&>button:not([data-disabled]):hover:has(+[data-pressed])]:rounded-e-none [&>button:not([data-disabled]):hover:has(+[data-pressed])]:before:-inset-e-(--toggle-hover-overlap)'
);

export type ButtonToggleGroupProps<TValue extends string> = Pick<
  React.ComponentPropsWithRef<'div'>,
  'id' | 'onBlur' | 'onFocus' | 'ref'
> & {
  bordered?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  fillContainer?: boolean;
  onChange: (value: TValue) => void;
  rounded?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  value: TValue | null | undefined;
  variant?: 'solid' | 'outline';
};

export function ButtonToggleGroup<TValue extends string>({
  bordered = true,
  children,
  disabled = false,
  fillContainer = false,
  onChange,
  rounded = false,
  size = 'md',
  value,
  variant = 'outline',
  ...props
}: ButtonToggleGroupProps<TValue>) {
  const layoutGroupId = useId();

  return (
    <LayoutGroup id={layoutGroupId}>
      <ToggleGroup<TValue>
        {...props}
        className={clsx(
          'isolate items-stretch bg-white',
          bordered &&
            'border border-gray-200 data-disabled:border-gray-200 data-disabled:bg-gray-50',
          rounded
            ? 'rounded-full [--toggle-radius:9999px]'
            : 'rounded-lg [--toggle-radius:var(--radius-md)]',
          variant === 'solid'
            ? '[--toggle-indicator-bg:var(--color-primary-600)] [--toggle-indicator-ring:transparent] [--toggle-indicator-text:var(--color-white)]'
            : '[--toggle-indicator-bg:var(--color-white)] [--toggle-indicator-ring:var(--color-primary-600)] [--toggle-indicator-text:var(--color-primary-600)]',
          hoverStyles,
          sizeStyles[size],
          fillContainer ? 'flex w-full [&>button]:flex-1' : 'inline-flex'
        )}
        disabled={disabled}
        multiple={false}
        onValueChange={(nextValues, eventDetails) => {
          const nextValue = nextValues[0];

          // Keep the active option selected when it is pressed again.
          if (typeof nextValue === 'undefined') {
            eventDetails.cancel();
            return;
          }

          onChange(nextValue);
        }}
        value={typeof value === 'string' ? [value] : []}
      >
        {children}
      </ToggleGroup>
    </LayoutGroup>
  );
}

export type ButtonToggleGroupOptionProps<TValue extends string> = Pick<
  React.ComponentPropsWithRef<'button'>,
  'ref'
> & {
  children?: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  value: TValue;
};

export function ButtonToggleGroupOption<TValue extends string>({
  children,
  disabled = false,
  icon,
  ...props
}: ButtonToggleGroupOptionProps<TValue>) {
  const hasText = !!children;
  const shouldReduceMotion = useReducedMotion();

  return (
    <Toggle<TValue>
      {...props}
      disabled={disabled}
      render={(buttonProps, state) => (
        <button
          {...buttonProps}
          className={clsx(
            'relative inline-flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-(--toggle-radius) py-(--toggle-py) text-sm font-medium whitespace-nowrap text-gray-600 select-none',
            hasText ? 'px-(--toggle-px)' : 'px-(--toggle-py)',
            'before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-[inherit]',
            'not-data-disabled:not-data-pressed:hover:before:bg-gray-100 not-data-disabled:not-data-pressed:active:before:bg-gray-100',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
            'data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:text-gray-400 not-data-disabled:data-pressed:text-(--toggle-indicator-text)'
          )}
        >
          {state.pressed && (
            <motion.span
              className={clsx(
                'pointer-events-none absolute inset-0 z-1 rounded-[inherit] shadow-xs ring-2 ring-inset',
                state.disabled
                  ? 'bg-gray-100 ring-gray-200'
                  : 'bg-(--toggle-indicator-bg) ring-(--toggle-indicator-ring)'
              )}
              initial={false}
              layoutId="indicator"
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : { damping: 35, stiffness: 500, type: 'spring' }
              }
            />
          )}

          {icon && (
            <span
              className={clsx(
                'relative z-2 flex shrink-0 items-center justify-center *:size-(--toggle-icon-size)',
                hasText && '-ml-0.5'
              )}
            >
              {icon}
            </span>
          )}

          {hasText && <span className="relative z-2 min-w-0 truncate">{children}</span>}
        </button>
      )}
    />
  );
}
